import { ApiRoutes } from '@utils/api-routes'
import { GACategory } from '@utils/google-analytics'
import GoogleTagManager from '@utils/gtm/google-tag-manager'
import http from '@utils/http'
import PageRoutes from '@utils/page-routes'

import type { NavigateParamsFunction } from '@hooks/useNavigateParams'
import type { SearchableObject } from '@models/SearchableObject'
import type { HTTPResponse } from '@utils/http'

export enum SearchCategory {
  YOUR_HOME = 'yourhome',
  SCORE = 'score',
  GROUP = 'group',
  COMMUNITY_MEMBER = 'communitymember',
  MARKETPLACE_PRODUCT = 'marketplaceproduct',
  MARKETPLACE_WITH_CONTRIBS = 'marketplace_with_contribs',
  TITLE = 'title',
}

export type SearchConfig = {
  [key in SearchCategory]?: number
}

export type SuggestionResults = {
  [key in SearchCategory]?: SearchableObject[]
}

export type SuggestionResponse = {
  results: SuggestionResults
}

export const defaultConfig: SearchConfig = {
  [SearchCategory.YOUR_HOME]: 3,
  [SearchCategory.SCORE]: 3,
  [SearchCategory.GROUP]: 1,
  [SearchCategory.COMMUNITY_MEMBER]: 1,
}

export default class SearchService {
  static async getSuggestions(
    term: string,
    config = defaultConfig
  ): HTTPResponse<SuggestionResponse> {
    GoogleTagManager.trackLegacyEvent(GACategory.SEARCH, 'suggestion', term)
    return http.post<SuggestionResponse>(ApiRoutes.apiSearchSuggestionsPath(), {
      term,
      search_types: config,
    })
  }

  static performSearch(
    searchConfigOrCategory: SearchConfig | SearchCategory,
    term: string,
    navigate: NavigateParamsFunction
  ): void {
    if (!searchConfigOrCategory) return

    const category: SearchCategory =
      typeof searchConfigOrCategory === 'object'
        ? (Object.keys(searchConfigOrCategory)[0] as SearchCategory)
        : searchConfigOrCategory
    switch (category) {
      case SearchCategory.MARKETPLACE_WITH_CONTRIBS: {
        const query = `keyword:${encodeURIComponent(term)}`
        GoogleTagManager.trackLegacyEvent(GACategory.SEARCH, 'product', encodeURIComponent(term))
        window.location.href = PageRoutes.marketplaceSearchQueryPath(query)
        break
      }
      case SearchCategory.GROUP: {
        GoogleTagManager.trackLegacyEvent(GACategory.SEARCH, 'group', encodeURIComponent(term))
        window.location.href = PageRoutes.groupsSearchPath({
          searchTarget: encodeURIComponent(term),
        })
        break
      }
      case SearchCategory.COMMUNITY_MEMBER: {
        GoogleTagManager.trackLegacyEvent(GACategory.SEARCH, 'member', encodeURIComponent(term))
        window.location.href = PageRoutes.publicMembersSearchPath({
          searchTarget: encodeURIComponent(term),
        })
        break
      }
      case SearchCategory.SCORE: {
        GoogleTagManager.trackLegacyEvent(
          GACategory.SEARCH,
          'public_score',
          encodeURIComponent(term)
        )
        window.location.href = PageRoutes.publicScoresSearchPath(
          `keyword:${encodeURIComponent(term)}`
        )
        break
      }
      case SearchCategory.YOUR_HOME: {
        GoogleTagManager.trackLegacyEvent(
          GACategory.SEARCH,
          'private_score',
          encodeURIComponent(term)
        )
        window.location.href = PageRoutes.myScoresPath({
          query: encodeURIComponent(term),
        })
        break
      }
      case SearchCategory.TITLE: {
        GoogleTagManager.trackLegacyEvent(GACategory.SEARCH, 'title', encodeURIComponent(term))
        navigate(PageRoutes.musicSearchPath(), { term })
        break
      }
      default:
        // Not implemented
        break
    }
  }
}
